import { useState } from 'react'
import { Modal } from '../components/common/Modal'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Input'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { RuleConditionBuilder } from '../components/strategy/RuleConditionBuilder'
import {
  useRules,
  useTheses,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  useToggleRule,
} from '../hooks/useThesis'
import type {
  Rule,
  RuleCreate,
  RuleUpdate,
  ConditionType,
  ConditionConfig,
  RuleAction,
  RulePriority,
} from '../types/thesis'
import { CONDITION_TYPE_LABELS, PRIORITY_COLORS } from '../types/thesis'

export function RulesPage() {
  const { data: rules, isLoading } = useRules()
  const { data: theses } = useTheses()
  const createRule = useCreateRule()
  const updateRule = useUpdateRule()
  const deleteRule = useDeleteRule()
  const toggleRule = useToggleRule()

  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<Rule | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Rule | null>(null)

  // Filter states
  const [thesisFilter, setThesisFilter] = useState<string>('')
  const [enabledFilter, setEnabledFilter] = useState<string>('')
  const [conditionFilter, setConditionFilter] = useState<string>('')

  // Form state
  const [formData, setFormData] = useState<{
    thesis_id: string
    name: string
    condition_type: ConditionType
    condition_config: ConditionConfig
    action: RuleAction
    priority: RulePriority
    cooldown_hours: number
    enabled: boolean
  }>({
    thesis_id: '',
    name: '',
    condition_type: 'price_below',
    condition_config: {},
    action: 'alert',
    priority: 'normal',
    cooldown_hours: 24,
    enabled: true,
  })
  const [error, setError] = useState('')

  const filteredRules = rules?.filter((r) => {
    if (thesisFilter && r.thesis_id !== thesisFilter) return false
    if (enabledFilter === 'enabled' && !r.enabled) return false
    if (enabledFilter === 'disabled' && r.enabled) return false
    if (conditionFilter && r.condition_type !== conditionFilter) return false
    return true
  })

  const resetForm = () => {
    setFormData({
      thesis_id: theses?.[0]?.id ?? '',
      name: '',
      condition_type: 'price_below',
      condition_config: {},
      action: 'alert',
      priority: 'normal',
      cooldown_hours: 24,
      enabled: true,
    })
    setError('')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }
    if (!formData.thesis_id) {
      setError('Please select a thesis')
      return
    }

    try {
      const createData: RuleCreate = {
        thesis_id: formData.thesis_id,
        name: formData.name,
        condition_type: formData.condition_type,
        condition_config: formData.condition_config,
        action: formData.action,
        priority: formData.priority,
        cooldown_hours: formData.cooldown_hours,
        enabled: formData.enabled,
      }
      await createRule.mutateAsync(createData)
      setIsCreateOpen(false)
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rule')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!editingRule) return
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    try {
      const updateData: RuleUpdate = {
        name: formData.name,
        condition_type: formData.condition_type,
        condition_config: formData.condition_config,
        action: formData.action,
        priority: formData.priority,
        cooldown_hours: formData.cooldown_hours,
        enabled: formData.enabled,
      }
      await updateRule.mutateAsync({ id: editingRule.id, data: updateData })
      setEditingRule(null)
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update rule')
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteRule.mutateAsync(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete rule')
    }
  }

  const handleToggle = async (rule: Rule) => {
    try {
      await toggleRule.mutateAsync({ id: rule.id, enabled: !rule.enabled })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle rule')
    }
  }

  const openEdit = (rule: Rule) => {
    setFormData({
      thesis_id: rule.thesis_id,
      name: rule.name,
      condition_type: rule.condition_type,
      condition_config: rule.condition_config,
      action: rule.action,
      priority: rule.priority,
      cooldown_hours: rule.cooldown_hours,
      enabled: rule.enabled,
    })
    setEditingRule(rule)
  }

  const openCreate = () => {
    resetForm()
    if (theses?.[0]) {
      setFormData((prev) => ({ ...prev, thesis_id: theses[0].id }))
    }
    setIsCreateOpen(true)
  }

  const formatConditionSummary = (rule: Rule): string => {
    const config = rule.condition_config as Record<string, unknown>
    switch (rule.condition_type) {
      case 'price_below':
      case 'price_above':
        return `${config.symbol} ${rule.condition_type === 'price_below' ? '<' : '>'} $${config.value}`
      case 'price_range':
        return `${config.symbol} $${config.value_low} - $${config.value_high}`
      case 'drawdown':
        return `${config.symbol} down ${config.threshold_pct}%`
      case 'ratio_above':
      case 'ratio_below':
        return `${config.numerator}/${config.denominator} ${rule.condition_type === 'ratio_above' ? '>' : '<'} ${config.threshold}`
      case 'crossover_above':
      case 'crossover_below':
        return `${config.indicator} ${rule.condition_type === 'crossover_above' ? 'crosses above' : 'crosses below'} ${config.threshold}`
      case 'concentration':
        return `Position > ${config.threshold_pct}%`
      default:
        return JSON.stringify(config)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const uniqueConditionTypes = [...new Set(rules?.map((r) => r.condition_type) ?? [])]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring Rules</h1>
          <p className="text-sm text-gray-500 mt-1">
            {rules?.filter((r) => r.enabled).length ?? 0} active of {rules?.length ?? 0} rules
          </p>
        </div>
        <Button onClick={openCreate}>New Rule</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Thesis</label>
          <select
            value={thesisFilter}
            onChange={(e) => setThesisFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All theses</option>
            {theses?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            value={conditionFilter}
            onChange={(e) => setConditionFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All types</option>
            {uniqueConditionTypes.map((type) => (
              <option key={type} value={type}>
                {CONDITION_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Rules Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rule
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thesis
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Condition
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRules && filteredRules.length > 0 ? (
              filteredRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(rule)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        rule.enabled ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          rule.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                    <div className="text-xs text-gray-500">
                      {CONDITION_TYPE_LABELS[rule.condition_type]}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.thesis_name ?? theses?.find((t) => t.id === rule.thesis_id)?.name ?? '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {formatConditionSummary(rule)}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[rule.priority]}`}
                    >
                      {rule.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => openEdit(rule)}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(rule)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No rules found. Create your first monitoring rule.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Create Rule" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Rule Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., IONQ Buy Zone Alert"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Thesis</label>
            <select
              value={formData.thesis_id}
              onChange={(e) => setFormData({ ...formData, thesis_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select thesis...</option>
              {theses?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <RuleConditionBuilder
            conditionType={formData.condition_type}
            conditionConfig={formData.condition_config}
            onTypeChange={(type) => setFormData({ ...formData, condition_type: type })}
            onConfigChange={(config) => setFormData({ ...formData, condition_config: config })}
            theses={theses}
          />

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as RulePriority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value as RuleAction })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="alert">Alert</option>
                <option value="log">Log Only</option>
                <option value="email">Email</option>
              </select>
            </div>
            <Input
              label="Cooldown (hours)"
              type="number"
              min={0}
              value={formData.cooldown_hours}
              onChange={(e) => setFormData({ ...formData, cooldown_hours: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-primary-600 rounded border-gray-300"
            />
            <label htmlFor="enabled" className="text-sm text-gray-700">
              Enable rule immediately
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createRule.isPending}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editingRule} onClose={() => setEditingRule(null)} title="Edit Rule" size="lg">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Rule Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <RuleConditionBuilder
            conditionType={formData.condition_type}
            conditionConfig={formData.condition_config}
            onTypeChange={(type) => setFormData({ ...formData, condition_type: type })}
            onConfigChange={(config) => setFormData({ ...formData, condition_config: config })}
            theses={theses}
          />

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as RulePriority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value as RuleAction })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="alert">Alert</option>
                <option value="log">Log Only</option>
                <option value="email">Email</option>
              </select>
            </div>
            <Input
              label="Cooldown (hours)"
              type="number"
              min={0}
              value={formData.cooldown_hours}
              onChange={(e) => setFormData({ ...formData, cooldown_hours: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit-enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-primary-600 rounded border-gray-300"
            />
            <label htmlFor="edit-enabled" className="text-sm text-gray-700">
              Enabled
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setEditingRule(null)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateRule.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Rule">
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteRule.isPending}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}
